import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthenticatedAxios } from '../utils/api';
import AuthenticatedEventSource from '../utils/AuthenticatedEventSource';
import { SESSION_EXPIRED_MESSAGE } from '../utils/authSession';
import { logAction, logError } from '../utils/frontLogger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  Users,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  Filter,
  Network,
  Activity,
  Zap,
  ArrowUpCircle,
  ArrowDownCircle,
  UserPlus,
  UserMinus,
  ExternalLink
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { getApiUrl } from '../utils/api';
import EnemyEntriesChart from '../components/EnemyEntriesChart';
import { useTokenConfig } from '../contexts/TokenConfigContext';
import HolderConnections from '../components/HolderConnections';

const API_URL = getApiUrl();

const TokenTracker = () => {
  // Token configuration from context
  const { tokenAddress: BUYT_TOKEN, tokenSymbol } = useTokenConfig();
  const requestGeneration = useRef(0);
  const streamRef = useRef(null);
  const [activeTab, setActiveTab] = useState('holders'); // 'holders' | 'connections' | 'activity'
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFromCache, setLoadingFromCache] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAll, setShowAll] = useState(true);  // Always show all by default
  const [sortBy, setSortBy] = useState('pnl'); // pnl, balance, avg_price
  const [filterType, setFilterType] = useState('all'); // all, our, others
  const [minBalanceUsd, setMinBalanceUsd] = useState(''); // Minimum balance filter in USD
  const [expandedHolder, setExpandedHolder] = useState(null);
  const [holderTransactions, setHolderTransactions] = useState(null);
  const [holderMetrics, setHolderMetrics] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [snapshotTime, setSnapshotTime] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0, message: '' });
  const [holders, setHolders] = useState([]);
  const [eventSource, setEventSource] = useState(null);
  const [ourWalletsSet, setOurWalletsSet] = useState(new Set());
  const [changesDetected, setChangesDetected] = useState({ new: 0, changed: 0, left: 0 });
  
  // Activity state
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activityStats, setActivityStats] = useState(null);

  useEffect(() => {
    if (!BUYT_TOKEN) return;
    requestGeneration.current += 1;
    // Сначала загружаем из кэша (мгновенно), потом обновляем
    loadCachedData();
    loadOurWallets();
    return () => {
      requestGeneration.current += 1;
      streamRef.current?.close();
    };
  }, [BUYT_TOKEN]);

  const loadOurWallets = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/wallets/list`);
      if (response.data && response.data.wallets && Array.isArray(response.data.wallets)) {
        const addresses = response.data.wallets.map(w => {
          const addr = w.address || '';
          return addr.toLowerCase();
        }).filter(Boolean);
        setOurWalletsSet(new Set(addresses));
        console.log(`Loaded ${addresses.length} our wallets`);
      }
    } catch (error) {
      console.error('Failed to load our wallets:', error);
    }
  };

  // НОВАЯ ФУНКЦИЯ: Мгновенная загрузка из кэша
  const loadCachedData = async () => {
    const generation = requestGeneration.current;
    setLoadingFromCache(true);
    setLoadError(null);
    
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/holder-activity/cached/${BUYT_TOKEN}`);
      
      if (generation !== requestGeneration.current) return;
      if (response.data.success && response.data.holders && response.data.holders.length > 0) {
        // Есть кэшированные данные - показываем мгновенно
        const cachedHolders = response.data.holders;
        setHolders(cachedHolders);
        setData({
          holders: cachedHolders,
          current_price: response.data.current_price_usd,
          total_holders: cachedHolders.length,
          total_tokens: response.data.stats?.total_tokens || cachedHolders.reduce((sum, h) => sum + (h.balance_tokens || 0), 0),
          total_pnl_usd: response.data.stats?.total_pnl_usd || cachedHolders.reduce((sum, h) => sum + (h.pnl_usd || 0), 0),
          our_wallets_count: response.data.stats?.our_wallets_count || cachedHolders.filter(h => h.is_our_wallet).length,
          our_total_tokens: response.data.stats?.our_total_tokens || 0,
          our_total_pnl_usd: response.data.stats?.our_total_pnl_usd || 0
        });
        setSnapshotTime(response.data.snapshot_time);
        setLastUpdate(new Date());
        
        toast.success(`Загружено ${cachedHolders.length} холдеров из кэша`, {
          description: 'Проверяем обновления...'
        });
        
        setLoadingFromCache(false);
        
        // После загрузки кэша - запускаем инкрементальное обновление
        runIncrementalUpdate();
      } else {
        // Нет кэша - запускаем полное сканирование
        toast.info('Кэш пуст, запускаем полное сканирование...');
        setLoadingFromCache(false);
        loadDataFull();
      }
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      console.error('Error loading cached data:', error);
      setLoadingFromCache(false);
      const status = error.response?.status;
      setLoadError(status === 401 ? SESSION_EXPIRED_MESSAGE : status === 403 ?
        'Недостаточно прав для загрузки данных.' : 'Не удалось загрузить данные. Повторите попытку.');
    }
  };

  // НОВАЯ ФУНКЦИЯ: Инкрементальное обновление
  const runIncrementalUpdate = () => {
    setIsUpdating(true);
    setProgress({ current: 0, total: 0, percent: 0, message: 'Проверяем изменения...' });
    
    if (eventSource) {
      eventSource.close();
    }
    
    const streamUrl = `/api/holder-activity/incremental-update/${BUYT_TOKEN}/stream`;
    
    streamRef.current?.close();
    setLoadError(null);
    const es = new AuthenticatedEventSource(streamUrl);
    streamRef.current = es;
    setEventSource(es);
    
    es.onmessage = (event) => {
      if (streamRef.current !== es || es.closed) return;
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'price') {
          setData(prev => ({
            ...prev,
            current_price: message.current_price
          }));
        } else if (message.type === 'cached_data') {
          // Уже загружено из кэша, можно пропустить
        } else if (message.type === 'status') {
          setProgress({
            current: message.progress || 0,
            total: message.total || 0,
            percent: message.total ? Math.round((message.progress / message.total) * 100) : 0,
            message: message.message || ''
          });
        } else if (message.type === 'changes_detected') {
          setChangesDetected({
            new: message.new || 0,
            changed: message.changed || 0,
            left: message.left || 0
          });
          
          if (message.new + message.changed > 0) {
            toast.info(`Найдено изменений: ${message.new} новых, ${message.changed} изменено`);
          } else if (message.unchanged > 0) {
            toast.success('Все холдеры актуальны');
          }
        } else if (message.type === 'holder_update') {
          // Обновить конкретного холдера в списке
          setHolders(prev => {
            const idx = prev.findIndex(h => 
              (h.address_raw || h.address) === message.holder.address_raw
            );
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], ...message.holder };
              return updated;
            } else {
              // Новый холдер
              return [...prev, message.holder];
            }
          });
          
          setProgress({
            current: message.progress,
            total: message.total,
            percent: Math.round((message.progress / message.total) * 100),
            message: `Анализ ${message.progress}/${message.total}...`
          });
        } else if (message.type === 'complete') {
          setData({
            current_price: message.current_price,
            total_holders: message.total_holders,
            total_tokens: message.total_tokens,
            total_pnl_usd: message.total_pnl_usd,
            our_wallets_count: message.our_wallets_count,
            our_total_tokens: message.our_total_tokens,
            our_total_pnl_usd: message.our_total_pnl_usd,
            holders: message.holders
          });
          setHolders(message.holders);
          setLastUpdate(new Date());
          setProgress({ current: 0, total: 0, percent: 100, message: 'Готово!' });
          setIsUpdating(false);
          es.close();
          
          toast.success(`Обновлено ${message.total_holders} холдеров`);
        } else if (message.type === 'error') {
          setLoadError(message.message || 'Не удалось загрузить данные');
          toast.error(`Ошибка: ${message.message}`);
          setIsUpdating(false);
          es.close();
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };
    
    es.onerror = (error) => {
      if (streamRef.current !== es) return;
      setLoadError(error.message || 'Не удалось загрузить данные. Повторите попытку.');
      console.error('Tracker stream failed:', error.status || 'network');
      setIsUpdating(false);
      es.close();
    };
  };

  // Полное сканирование - использует тот же incremental endpoint
  // но начинает с нуля (без кэша)
  const loadDataFull = () => {
    setLoading(true);
    setProgress({ current: 0, total: 0, percent: 0, message: 'Запуск полного сканирования...' });
    setHolders([]);
    setData(null);
    
    if (eventSource) {
      eventSource.close();
    }
    
    // Используем тот же incremental endpoint - он сохраняет в базу
    const streamUrl = `/api/holder-activity/incremental-update/${BUYT_TOKEN}/stream`;
    
    streamRef.current?.close();
    setLoadError(null);
    const es = new AuthenticatedEventSource(streamUrl);
    streamRef.current = es;
    setEventSource(es);
    
    let holdersList = [];
    
    es.onmessage = (event) => {
      if (streamRef.current !== es || es.closed) return;
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'status') {
          setProgress({
            current: message.progress || 0,
            total: message.total || 0,
            percent: message.total ? Math.round((message.progress / message.total) * 100) : 0,
            message: message.message || ''
          });
        } else if (message.type === 'price') {
          setData(prev => ({
            ...prev,
            current_price: message.current_price,
            timestamp: new Date().toISOString()
          }));
        } else if (message.type === 'cached_data') {
          // При полном сканировании пропускаем кэшированные данные
          // Но обновляем UI для отображения прогресса
          if (message.holders && message.holders.length > 0) {
            setHolders(message.holders);
            holdersList = message.holders;
          }
        } else if (message.type === 'changes_detected') {
          setChangesDetected({
            new: message.new || 0,
            changed: message.changed || 0,
            left: message.left || 0
          });
        } else if (message.type === 'holder_update') {
          // Обновить конкретного холдера
          const idx = holdersList.findIndex(h => 
            (h.address_raw || h.address) === message.holder.address_raw
          );
          if (idx >= 0) {
            holdersList[idx] = { ...holdersList[idx], ...message.holder };
          } else {
            holdersList.push(message.holder);
          }
          setHolders([...holdersList]);
          
          setProgress({
            current: message.progress,
            total: message.total,
            percent: Math.round((message.progress / message.total) * 100),
            message: `Анализ ${message.progress}/${message.total}...`
          });
        } else if (message.type === 'complete') {
          // Финальный результат
          setData({
            token_address: message.token_address,
            current_price: message.current_price,
            timestamp: message.timestamp,
            total_holders: message.total_holders,
            total_tokens: message.total_tokens,
            total_pnl_usd: message.total_pnl_usd,
            our_wallets_count: message.our_wallets_count,
            our_total_tokens: message.our_total_tokens,
            our_total_pnl_usd: message.our_total_pnl_usd,
            holders: message.holders
          });
          setHolders(message.holders);
          setProgress({
            current: message.total_holders,
            total: message.total_holders,
            percent: 100,
            message: 'Готово! Данные сохранены в кэш.'
          });
          setLastUpdate(new Date());
          toast.success(`Загружено ${message.total_holders} холдеров и сохранено в кэш`);
          es.close();
          setLoading(false);
        } else if (message.type === 'error') {
          setLoadError(message.message || 'Не удалось загрузить данные');
          toast.error(`Ошибка: ${message.message}`);
          setProgress({
            current: 0,
            total: 0,
            percent: 0,
            message: `Ошибка: ${message.message}`
          });
          es.close();
          setLoading(false);
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };
    
    es.onerror = (error) => {
      if (streamRef.current !== es) return;
      setLoadError(error.message || 'Не удалось загрузить данные. Повторите попытку.');
      console.error('Tracker stream failed:', error.status || 'network');
      toast.error('Ошибка соединения');
      es.close();
      setLoading(false);
    };
  };
  
  const loadDataFallback = async () => {
    try {
      const limit = showAll ? 10000 : 10000;  // Always show all holders
      const api = getAuthenticatedAxios();
      const response = await api.get(
        `${API_URL}/api/token-analytics/holders/${BUYT_TOKEN}?limit=${limit}`
      );

      if (response.data.success) {
        const apiData = response.data.data || response.data;
        
        if (apiData.error && apiData.holders?.length === 0) {
          toast.warning(`Could not fetch holders: ${apiData.error}`);
        } else if (apiData.holders?.length === 0) {
          toast.warning('No holders found for this token');
        } else {
          toast.success(`Loaded ${apiData.holders?.length || 0} holders`);
        }
        
        setData(apiData);
        setHolders(apiData.holders || []);
        setLastUpdate(new Date());
      } else {
        console.error('TokenTracker: API returned success=false', response.data);
        toast.error('Failed to load holder data: API error');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load holder data');
    } finally {
      setLoading(false);
    }
  };

  // ФУНКЦИИ ДЛЯ АКТИВНОСТИ
  const loadActivities = async () => {
    setLoadingActivities(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/holder-activity/activity/${BUYT_TOKEN}?limit=100`);
      
      if (response.data.success) {
        setActivities(response.data.activities || []);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      toast.error('Не удалось загрузить активность');
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadActivityStats = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/holder-activity/activity-stats/${BUYT_TOKEN}?hours=24`);
      
      if (response.data.success) {
        setActivityStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading activity stats:', error);
    }
  };

  // Загружать активность при переключении на вкладку
  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivities();
      loadActivityStats();
    }
  }, [activeTab]);

  // Форматирование типа активности
  const formatActivityType = (type) => {
    switch (type) {
      case 'new_holder': return { label: 'Новый холдер', icon: UserPlus, color: 'text-green-600', bg: 'bg-green-50' };
      case 'left_holder': return { label: 'Холдер ушел', icon: UserMinus, color: 'text-red-600', bg: 'bg-red-50' };
      case 'balance_up': return { label: 'Покупка', icon: ArrowUpCircle, color: 'text-green-600', bg: 'bg-green-50' };
      case 'balance_down': return { label: 'Продажа', icon: ArrowDownCircle, color: 'text-red-600', bg: 'bg-red-50' };
      default: return { label: type, icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  // Format address for display - show full address if user-friendly, truncated if raw
  const formatAddress = (addr) => {
    if (!addr) return '';
    // If user-friendly format (EQ/UQ), show truncated
    if (addr.startsWith('EQ') || addr.startsWith('UQ') || addr.startsWith('kQ')) {
      if (addr.length > 12) {
        return `${addr.substring(0, 6)}...${addr.substring(addr.length - 6)}`;
      }
      return addr;
    }
    // If raw format (0:hex), show more characters for readability
    if (addr.startsWith('0:') || addr.startsWith('-1:')) {
      if (addr.length > 20) {
        return `${addr.substring(0, 10)}...${addr.substring(addr.length - 8)}`;
      }
      return addr;
    }
    // Default truncation
    if (addr.length > 12) {
      return `${addr.substring(0, 6)}...${addr.substring(addr.length - 6)}`;
    }
    return addr;
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatTime = (hours) => {
    if (hours === undefined || hours === null || isNaN(hours)) return '0h';
    if (hours < 24) return `${hours.toFixed(1)}h`;
    const days = hours / 24;
    if (days < 30) return `${days.toFixed(1)}d`;
    return `${(days / 30).toFixed(1)}mo`;
  };

  const loadHolderTransactions = async (walletAddress, forceRefresh = false) => {
    logAction('holder_tx_load', {
      target: walletAddress?.slice(0, 32) || '',
      extra: { force_refresh: forceRefresh }
    });
    setLoadingTransactions(true);
    try {
      // Encode address for URL
      const encodedAddress = encodeURIComponent(walletAddress);
      const api = getAuthenticatedAxios();
      const url = `${API_URL}/api/token/${BUYT_TOKEN}/wallet/${encodedAddress}/transactions`;
      
      console.log(`Loading transactions for wallet: ${walletAddress.substring(0, 30)}... ${forceRefresh ? '(FORCE REFRESH)' : ''}`);
      
      const response = await api.get(url, { 
        params: { limit: 10000, force_refresh: forceRefresh },
        timeout: 60000  // 60 second timeout for blockchain sync
      });
      
      console.log('Transaction response:', response.data);
      
      if (response.data) {
        const transactions = response.data.transactions || [];
        const metrics = response.data.metrics || null;
        
        console.log(`Loaded ${transactions.length} transactions`);
        console.log('Metrics:', metrics);
        
        setHolderTransactions(transactions);
        setHolderMetrics(metrics);
        
        // Check history status first
        if (metrics?.history_status === 'transfer_only') {
          toast.info('📦 Токены получены трансфером (не через DEX)', { duration: 4000 });
        } else if (metrics?.history_status === 'unavailable') {
          toast.info('История не найдена ни через Toncenter, ни через TonAPI. Попробуйте "Обновить".', {
            duration: 5000
          });
        } else if (transactions.length === 0 && (!metrics || (metrics.total_bought === 0 && metrics.total_sold === 0))) {
          toast.warning('Транзакции не найдены для этого кошелька');
        } else if (transactions.length > 0) {
          toast.success(`Загружено ${transactions.length} транзакций`);
        }
      }
    } catch (error) {
      console.error('Failed to load holder transactions:', error);
      console.error('Error response:', error.response?.data);
      logError(error, { ctx: 'loadHolderTransactions', wallet: walletAddress?.slice(0, 32) });
      toast.error(`Не удалось загрузить транзакции: ${error.response?.data?.detail || error.message}`);
      setHolderTransactions([]);
      setHolderMetrics(null);
    } finally {
      setLoadingTransactions(false);
    }
  };

  if (!data && (loading || loadingFromCache)) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-blue-600" />
            <p className="text-gray-600">Загрузка данных холдеров…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data && !loadingFromCache) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p role={loadError ? "alert" : "status"} className="text-gray-600 mb-4">{loadError || "Данные холдеров еще не загружены"}</p>
            <Button onClick={loadCachedData} disabled={loading || loadingFromCache}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading || loadingFromCache ? 'animate-spin' : ''}`} />
              Загрузить данные
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Filter holders - use holders state if available, otherwise use data.holders
  const holdersToFilter = holders.length > 0 ? holders : (data?.holders || []);
  
  // Step 1: Apply minimum balance filter first (for statistics calculation)
  let holdersAfterBalanceFilter = holdersToFilter;
  if (minBalanceUsd && !isNaN(parseFloat(minBalanceUsd))) {
    const minBalance = parseFloat(minBalanceUsd);
    holdersAfterBalanceFilter = holdersAfterBalanceFilter.filter(h => (h.balance_usd || 0) >= minBalance);
  }
  
  // Step 2: Apply type filter (our/others/all) for table display
  let filteredHolders = holdersAfterBalanceFilter;
  if (filterType === 'our') {
    filteredHolders = filteredHolders.filter(h => h.is_our_wallet);
  } else if (filterType === 'others') {
    filteredHolders = filteredHolders.filter(h => !h.is_our_wallet);
  }

  // Sort holders
  const sortedHolders = [...filteredHolders].sort((a, b) => {
    if (sortBy === 'pnl') {
      return (b.pnl_usd || 0) - (a.pnl_usd || 0);
    } else if (sortBy === 'balance') {
      return (b.balance_tokens || 0) - (a.balance_tokens || 0);
    } else if (sortBy === 'avg_price') {
      return (b.avg_buy_price || 0) - (a.avg_buy_price || 0);
    }
    return 0;
  });

  // Компонент табов (переиспользуемый)
  const TabButtons = () => (
        <div className="flex items-center gap-4 border-b pb-4">
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'holders' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            onClick={() => setActiveTab('holders')}
          >
            <Users className="h-4 w-4" />
            Холдеры
          </button>
      <button
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
          activeTab === 'activity' 
            ? 'bg-purple-600 text-white' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        onClick={() => setActiveTab('activity')}
      >
        <Activity className="h-4 w-4" />
        Активность
        {changesDetected.new + changesDetected.changed > 0 && (
          <Badge className="ml-1 bg-red-500 text-white text-xs">
            {changesDetected.new + changesDetected.changed}
          </Badge>
        )}
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'connections' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            onClick={() => setActiveTab('connections')}
          >
            <Network className="h-4 w-4" />
            Связи между холдерами
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'price'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            onClick={() => setActiveTab('price')}
          >
            📈 Цена / входы врагов
          </button>
        </div>
  );
        
  // If connections tab is active, show only connections component
  if (activeTab === 'price') {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <TabButtons />
        <EnemyEntriesChart />
      </div>
    );
  }

  if (activeTab === 'connections') {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <TabButtons />
        <HolderConnections />
      </div>
    );
  }

  // If activity tab is active, show activity component
  if (activeTab === 'activity') {
  return (
    <div className="container mx-auto p-6 space-y-6">
        <TabButtons />
        
        {/* Activity Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Активность холдеров</h1>
            <p className="text-gray-500 mt-1">
              История покупок, продаж и изменений
            </p>
          </div>
          <Button onClick={loadActivities} disabled={loadingActivities}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingActivities ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>

        {/* Activity Stats */}
        {activityStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-green-600" />
                  Новые холдеры
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {activityStats.by_type?.new_holder?.total || 0}
                </div>
                <p className="text-xs text-gray-500">за 24 часа</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <UserMinus className="h-4 w-4 text-red-600" />
                  Ушли
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {activityStats.by_type?.left_holder?.total || 0}
                </div>
                <p className="text-xs text-gray-500">за 24 часа</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <ArrowDownCircle className="h-4 w-4 text-orange-600" />
                  Продажи
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {activityStats.sales_count || 0}
                </div>
                <p className="text-xs text-gray-500">за 24 часа</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Суммарный PNL
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${(activityStats.total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${Math.abs(activityStats.total_pnl_usd || 0).toFixed(2)}
                </div>
                <p className="text-xs text-gray-500">
                  {(activityStats.total_pnl_ton || 0).toFixed(4)} TON
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity List */}
        <Card>
          <CardHeader>
            <CardTitle>История активности</CardTitle>
            <CardDescription>
              Последние {activities.length} событий
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingActivities ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Загрузка...</span>
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Нет активности для отображения</p>
                <p className="text-sm mt-2">
                  Активность начнет записываться после первого обновления данных
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {activities.map((activity, idx) => {
                  const activityInfo = formatActivityType(activity.activity_type);
                  const ActivityIcon = activityInfo.icon;
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg ${activityInfo.bg} ${activity.is_our_wallet ? 'border-2 border-blue-300' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <ActivityIcon className={`h-5 w-5 ${activityInfo.color}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${activityInfo.color}`}>
                              {activityInfo.label}
                            </span>
                            {activity.is_our_wallet && (
                              <Badge className="bg-blue-600 text-white text-xs">OUR</Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 font-mono flex items-center gap-1.5">
                            <span>{formatAddress(activity.wallet_address_friendly || activity.wallet_address)}</span>
                            <a
                              href={`https://tonviewer.com/${activity.wallet_address_friendly || activity.wallet_address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:text-blue-800"
                              title="Открыть в Tonviewer"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(activity.activity_time).toLocaleString()}
                          </div>
                        </div>
      </div>
                      
                      <div className="text-right">
                        {activity.balance_change !== null && (
                          <div className={`font-medium ${activity.balance_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {activity.balance_change >= 0 ? '+' : ''}{formatNumber(activity.balance_change)} {tokenSymbol || 'TOKEN'}
                          </div>
                        )}
                        {activity.pnl_usd !== null && (
                          <div className={`text-sm ${activity.pnl_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            PNL: {activity.pnl_usd >= 0 ? '+' : ''}${activity.pnl_usd.toFixed(2)}
                            {activity.pnl_percent !== null && (
                              <span className="ml-1">
                                ({activity.pnl_percent >= 0 ? '+' : ''}{activity.pnl_percent.toFixed(1)}%)
                              </span>
                            )}
                          </div>
                        )}
                        {activity.new_balance !== null && (
                          <div className="text-xs text-gray-500">
                            Баланс: {formatNumber(activity.new_balance)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Tabs */}
      <TabButtons />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Token Holder Analytics</h1>
          <p className="text-gray-500 mt-1">
            {loadingFromCache ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Загрузка из кэша...
              </span>
            ) : isUpdating && progress.total > 0 ? (
              <span className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                {progress.message} ({progress.current}/{progress.total} - {progress.percent}%)
              </span>
            ) : loading && progress.total > 0 ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                {progress.message} ({progress.current}/{progress.total} - {progress.percent}%)
              </span>
            ) : (
              <>
                {tokenSymbol || 'TOKEN'} Token · Current Price: ${data?.current_price?.toFixed(8) || '0.00000000'}
                {snapshotTime && (
                  <span className="ml-2 text-xs text-gray-400">
                    · Снапшот: {new Date(snapshotTime).toLocaleString()}
                  </span>
                )}
                {lastUpdate && (
                  <span className="ml-2 text-xs text-gray-400">
                    · Обновлено: {new Date(lastUpdate).toLocaleTimeString()}
                  </span>
                )}
                {isUpdating && (
                  <span className="ml-2 text-xs text-yellow-600 flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Обновление...
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          {(isUpdating || loading) && progress.total > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${isUpdating ? 'bg-yellow-500' : 'bg-blue-600'}`}
                  style={{ width: `${progress.percent}%` }}
                ></div>
              </div>
              <span className="font-medium">{progress.percent}%</span>
            </div>
          )}
          <Button 
            onClick={runIncrementalUpdate} 
            disabled={loading || isUpdating}
            variant="outline"
          >
            <Zap className={`h-4 w-4 mr-2 ${isUpdating ? 'animate-pulse text-yellow-500' : ''}`} />
            Быстрое обновление
          </Button>
          <Button onClick={loadDataFull} disabled={loading || isUpdating}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Полное сканирование
          </Button>
        </div>
      </div>

      {/* Progress Card */}
      {loading && progress.total > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Loading Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{progress.message}</span>
                <span className="font-medium">{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                  style={{ width: `${progress.percent}%` }}
                >
                  <span className="text-xs text-white font-medium">{progress.percent}%</span>
                </div>
              </div>
              {holders.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {holders.length} holders loaded so far...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Holders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.total_holders || holders.length || 0}</div>
              {loading && holders.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {holders.length} loaded so far...
                </p>
              )}
            </CardContent>
          </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(data.total_tokens || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total P/L
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(data.total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${Math.abs(data.total_pnl_usd || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Our Wallets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.our_wallets_count || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              P/L: <span className={(data.our_total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                ${Math.abs(data.our_total_pnl_usd || 0).toFixed(2)}
              </span>
            </p>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Tokens Summary - Our vs Others */}
      {data && holders.length > 0 && (() => {
        // Calculate statistics for our and others' tokens using filtered holders
        // Use holdersAfterBalanceFilter to include balance filter but exclude type filter
        const ourHolders = holdersAfterBalanceFilter.filter(h => h.is_our_wallet || ourWalletsSet.has((h.address_raw || h.address)?.toLowerCase() || ''));
        const othersHolders = holdersAfterBalanceFilter.filter(h => !h.is_our_wallet && !ourWalletsSet.has((h.address_raw || h.address)?.toLowerCase() || '') && !h.is_amm);
        
        const ourTotalTokens = ourHolders.reduce((sum, h) => sum + (h.balance_tokens || 0), 0);
        const othersTotalTokens = othersHolders.reduce((sum, h) => sum + (h.balance_tokens || 0), 0);
        
        const ourTotalPnl = ourHolders.reduce((sum, h) => sum + (h.pnl_usd || 0), 0);
        const othersTotalPnl = othersHolders.reduce((sum, h) => sum + (h.pnl_usd || 0), 0);

        const currentPrice = data.current_price || 0;
        const currentPriceTon = data.current_price_ton || 0;

        // token-tracker audit T1: VWAP (balance-weighted average buy price) из backend
        const ourVwapTon = data.our_vwap_buy_price_ton || 0;
        const ourVwapUsd = data.our_vwap_buy_price_usd || 0;
        const ourVwapCount = data.our_vwap_holders_count || 0;
        const enemyVwapTon = data.enemy_vwap_buy_price_ton || 0;
        const enemyVwapUsd = data.enemy_vwap_buy_price_usd || 0;
        const enemyVwapCount = data.enemy_vwap_holders_count || 0;
        const ourPremiumPct = data.our_price_premium_pct || 0;
        const enemyPremiumPct = data.enemy_price_premium_pct || 0;
        const holdersWithHistory = data.holders_with_history || 0;
        const holdersTransferOnly = data.holders_transfer_only || 0;
        const holdersUnavailable = data.holders_unavailable || 0;

        return (
          <div className="space-y-4">
            {/* T1: VWAP Summary — 4 карточки: наши, враги, цена, премия */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-gray-600 font-medium">Наш VWAP</p>
                  <p className="text-lg font-bold text-green-700">
                    ${ourVwapUsd > 0 ? ourVwapUsd.toFixed(9) : '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {ourVwapTon > 0 ? `${(ourVwapTon * 1e9).toFixed(3)} nanoTON` : ''}
                  </p>
                  <p className="text-xs text-gray-500">{ourVwapCount} кошельков</p>
                </CardContent>
              </Card>

              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-gray-600 font-medium">VWAP врагов</p>
                  <p className="text-lg font-bold text-orange-700">
                    ${enemyVwapUsd > 0 ? enemyVwapUsd.toFixed(9) : '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {enemyVwapTon > 0 ? `${(enemyVwapTon * 1e9).toFixed(3)} nanoTON` : ''}
                  </p>
                  <p className="text-xs text-gray-500">{enemyVwapCount} кошельков</p>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-gray-600 font-medium">Текущая цена</p>
                  <p className="text-lg font-bold text-blue-700">
                    ${currentPrice > 0 ? currentPrice.toFixed(9) : '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentPriceTon > 0 ? `${(currentPriceTon * 1e9).toFixed(3)} nanoTON` : ''}
                  </p>
                  <p className="text-xs text-gray-500">market price</p>
                </CardContent>
              </Card>

              <Card className={`${ourPremiumPct >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <CardContent className="pt-4">
                  <p className="text-xs text-gray-600 font-medium">Наша премия vs рынок</p>
                  <p className={`text-lg font-bold ${ourPremiumPct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {ourPremiumPct >= 0 ? '+' : ''}{ourPremiumPct.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Враги: {enemyPremiumPct >= 0 ? '+' : ''}{enemyPremiumPct.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500">
                    {ourPremiumPct > enemyPremiumPct
                      ? `на ${(ourPremiumPct - enemyPremiumPct).toFixed(1)}pp лучше врагов`
                      : `на ${(enemyPremiumPct - ourPremiumPct).toFixed(1)}pp хуже врагов`}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* T4: History status breakdown */}
            {(holdersTransferOnly > 0 || holdersUnavailable > 0) && (
              <div className="text-xs text-gray-500 px-2">
                Холдеров с полной историей: <span className="font-semibold text-gray-700">{holdersWithHistory}</span>
                {holdersTransferOnly > 0 && (
                  <> · получено трансфером: <span className="font-semibold text-amber-700">{holdersTransferOnly}</span></>
                )}
                {holdersUnavailable > 0 && (
                  <> · нет истории: <span className="font-semibold text-red-700">{holdersUnavailable}</span></>
                )}
              </div>
            )}

            {/* Existing "наши/враги" cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Our Wallets Summary */}
            {ourHolders.length > 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    Наши токены
                    {minBalanceUsd && (
                      <span className="text-xs font-normal text-gray-500">(мин. ${parseFloat(minBalanceUsd).toFixed(0)})</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Всего наших токенов</p>
                      <p className="text-2xl font-bold text-green-700">{formatNumber(ourTotalTokens)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        ~${(ourTotalTokens * currentPrice).toFixed(2)} USD
                      </p>
                    </div>
                    <div className="border-t pt-2">
                      <p className="text-sm text-gray-600">Кошельков</p>
                      <p className="text-lg font-semibold">{ourHolders.length}</p>
                    </div>
                    <div className="border-t pt-2">
                      <p className="text-sm text-gray-600">Total P/L</p>
                      <p className={`text-xl font-bold ${ourTotalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ourTotalPnl >= 0 ? '+' : ''}${ourTotalPnl.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Others' Wallets Summary */}
            {othersHolders.length > 0 && (
              <Card className="bg-gray-50 border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserMinus className="h-5 w-5 text-gray-600" />
                    Чужие токены
                    {minBalanceUsd && (
                      <span className="text-xs font-normal text-gray-500">(мин. ${parseFloat(minBalanceUsd).toFixed(0)})</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Всего чужих токенов</p>
                      <p className="text-2xl font-bold text-gray-700">{formatNumber(othersTotalTokens)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        ~${(othersTotalTokens * currentPrice).toFixed(2)} USD
                      </p>
                    </div>
                    <div className="border-t pt-2">
                      <p className="text-sm text-gray-600">Кошельков</p>
                      <p className="text-lg font-semibold">{othersHolders.length}</p>
                    </div>
                    <div className="border-t pt-2">
                      <p className="text-sm text-gray-600">Total P/L</p>
                      <p className={`text-xl font-bold ${othersTotalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {othersTotalPnl >= 0 ? '+' : ''}${othersTotalPnl.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            </div>
          </div>
        );
      })()}

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* First row: Type filter and Min Balance */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">Filter:</span>
                  <Button
                    size="sm"
                    variant={filterType === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterType('all')}
                  >
                    All ({holders.length > 0 ? holders.length : (data?.holders?.length || 0)})
                  </Button>
                  <Button
                    size="sm"
                    variant={filterType === 'our' ? 'default' : 'outline'}
                    onClick={() => setFilterType('our')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Our ({data?.our_wallets_count || holders.filter(h => h.is_our_wallet).length || 0})
                  </Button>
                  <Button
                    size="sm"
                    variant={filterType === 'others' ? 'default' : 'outline'}
                    onClick={() => setFilterType('others')}
                  >
                    Others ({holders.length > 0 ? holders.filter(h => !h.is_our_wallet).length : ((data?.holders?.length || 0) - (data?.our_wallets_count || 0))})
                  </Button>
                </div>
              </div>

              {/* Min Balance Filter */}
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Min Balance (USD):</span>
                <input
                  type="number"
                  value={minBalanceUsd}
                  onChange={(e) => setMinBalanceUsd(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                  className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {minBalanceUsd && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setMinBalanceUsd('')}
                    className="h-8 w-8 p-0"
                  >
                    ✕
                  </Button>
                )}
              </div>
            </div>

            {/* Second row: Sort options */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort by:</span>
              <Button
                size="sm"
                variant={sortBy === 'pnl' ? 'default' : 'outline'}
                onClick={() => setSortBy('pnl')}
              >
                P/L
              </Button>
              <Button
                size="sm"
                variant={sortBy === 'balance' ? 'default' : 'outline'}
                onClick={() => setSortBy('balance')}
              >
                Balance
              </Button>
              <Button
                size="sm"
                variant={sortBy === 'avg_price' ? 'default' : 'outline'}
                onClick={() => setSortBy('avg_price')}
              >
                Avg Price
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Holders ({sortedHolders.length})</CardTitle>
          <CardDescription>
            {loading && holders.length > 0 ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading... {holders.length} holders loaded so far
              </span>
            ) : (
              'Click on a holder to see detailed trade history'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && holders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-blue-600" />
              <p>Loading holders...</p>
              {progress.message && <p className="text-sm mt-2">{progress.message}</p>}
            </div>
          )}
          
          {!loading && sortedHolders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg font-medium mb-2">No holders found</p>
              {data?.error ? (
                <div className="space-y-2">
                  <p className="text-sm text-red-600">Error: {data.error}</p>
                  <p className="text-xs text-gray-400">
                    This might be due to TonAPI rate limits. Please try again later or configure an API key.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">Total holders: {data?.total_holders || 0}</p>
                  <p className="text-xs">
                    Filter: {filterType} | 
                    Available: {data?.holders?.length || holders.length || 0}
                  </p>
                  {filterType !== 'all' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFilterType('all')}
                      className="mt-4"
                    >
                      Show All Holders
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {sortedHolders.length > 0 && (
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wallet</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Balance (Tokens)</TableHead>
                <TableHead>Balance (USD)</TableHead>
                <TableHead>Avg Buy Price</TableHead>
                <TableHead>P/L (USD)</TableHead>
                <TableHead>P/L (%)</TableHead>
                <TableHead>Price Change</TableHead>
                <TableHead>Holding Time</TableHead>
                <TableHead>Trades</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedHolders.map((holder, index) => (
                <React.Fragment key={holder.address}>
                  <TableRow 
                    className={`
                      cursor-pointer hover:bg-gray-50 
                      ${holder.is_our_wallet || ourWalletsSet.has((holder.address_raw || holder.address)?.toLowerCase() || '') ? 'bg-green-50' : ''}
                      ${expandedHolder === holder.address ? 'border-b-0' : ''}
                    `}
                    onClick={async () => {
                      const newExpanded = expandedHolder === holder.address ? null : holder.address;
                      setExpandedHolder(newExpanded);
                      
                      // Load transactions when expanding
                      if (newExpanded) {
                        // Use raw address if available (for API calls), otherwise use address as-is
                        const addressToUse = holder.address_raw || holder.address;
                        await loadHolderTransactions(addressToUse);
                      } else {
                        setHolderTransactions(null);
                        setHolderMetrics(null);
                      }
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {expandedHolder === holder.address ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        {holder.label ? (
                          <span className="font-semibold text-sm text-indigo-700" title={holder.address}>
                            {holder.label}
                          </span>
                        ) : (
                          <span className="font-mono text-sm" title={holder.address}>
                            {formatAddress(holder.address)}
                          </span>
                        )}
                        {/* Отдельная кнопка-ссылка на Tonviewer. stopPropagation
                            чтобы не раскрывать строку при клике. */}
                        <a
                          href={`https://tonviewer.com/${holder.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
                          title={`Открыть ${holder.address} в Tonviewer`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Tonviewer
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      {holder.is_amm ? (
                        <Badge variant="default" className="text-xs bg-purple-600">
                          <Network className="h-3 w-3 mr-1" />
                          AMM STON.fi
                        </Badge>
                      ) : (holder.is_our_wallet || ourWalletsSet.has((holder.address_raw || holder.address)?.toLowerCase() || '')) ? (
                        <Badge variant="default" className="text-xs bg-green-600">
                          <Users className="h-3 w-3 mr-1" />
                          Наш
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-gray-600">
                          <UserMinus className="h-3 w-3 mr-1" />
                          Чужой
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatNumber(holder.balance_tokens || 0)}
                    </TableCell>
                    <TableCell>${(holder.balance_usd || 0).toFixed(2)}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {holder.history_status === 'unavailable' ? (
                        <div className="text-gray-400 text-xs flex items-center gap-1">
                          <span>📜</span>
                          <span>История недоступна</span>
                        </div>
                      ) : holder.avg_buy_price_ton !== undefined ? (
                        <div>
                          <div>{(holder.avg_buy_price_ton || 0).toFixed(8)} TON</div>
                          <div className="text-xs text-gray-500">${(holder.avg_buy_price_usd || holder.avg_buy_price || 0).toFixed(8)}</div>
                        </div>
                      ) : (
                        <div>${(holder.avg_buy_price || 0).toFixed(8)}</div>
                      )}
                    </TableCell>
                    <TableCell className={(holder.pnl_ton !== undefined ? holder.pnl_ton : holder.pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      <div className="font-bold">
                        {holder.pnl_ton !== undefined ? (
                          <div>
                            <div>{(holder.pnl_ton || 0) >= 0 ? '+' : ''}{(holder.pnl_ton || 0).toFixed(4)} TON</div>
                            <div className="text-xs">${(holder.pnl_usd || 0) >= 0 ? '+' : ''}${(holder.pnl_usd || 0).toFixed(2)}</div>
                          </div>
                        ) : (
                          <div>{(holder.pnl_usd || 0) >= 0 ? '+' : ''}${(holder.pnl_usd || 0).toFixed(2)}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={(holder.pnl_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      <div className="font-bold">
                        {(holder.pnl_percent || 0) >= 0 ? '+' : ''}{(holder.pnl_percent || 0).toFixed(1)}%
                      </div>
                    </TableCell>
                    <TableCell className={(holder.price_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {(holder.price_change_percent || 0) >= 0 ? '+' : ''}{(holder.price_change_percent || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-gray-400" />
                      {formatTime(holder.holding_time_hours || 0)}
                    </TableCell>
                    <TableCell>
                      {holder.history_status === 'unavailable' ? (
                        <span className="text-gray-400 text-xs">N/A</span>
                      ) : (
                        <>
                          <span className="text-green-600">{holder.buy_count || 0}↑</span>
                          {' / '}
                          <span className="text-red-600">{holder.sell_count || 0}↓</span>
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Trade History */}
                  {expandedHolder === holder.address && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-gray-50 p-4">
                        {loadingTransactions ? (
                          <div className="flex items-center justify-center py-8">
                            <RefreshCw className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                            <span className="text-gray-600">Загрузка транзакций...</span>
                          </div>
                        ) : holderMetrics ? (
                          <div className="space-y-4">
                            {/* Metrics Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Средняя цена закупки</div>
                                <div className="font-semibold">
                                  {holderMetrics.history_status === 'unavailable' ? (
                                    <div className="flex items-center gap-1 text-gray-400">
                                      <span>📜</span>
                                      <span className="text-xs">История недоступна</span>
                                    </div>
                                  ) : holderMetrics.avg_buy_price_ton > 0 ? (
                                    <>
                                      <div>{holderMetrics.avg_buy_price_ton.toFixed(8)} TON</div>
                                      <div className="text-xs text-gray-500">${holderMetrics.avg_buy_price_usd.toFixed(8)}</div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Нет покупок</span>
                                  )}
                                </div>
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Текущий PNL</div>
                                <div className={`font-semibold ${holderMetrics.pnl_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {holderMetrics.current_balance > 0 ? (
                                    <>
                                      <div>{holderMetrics.pnl_ton >= 0 ? '+' : ''}{holderMetrics.pnl_ton.toFixed(4)} TON</div>
                                      <div className="text-xs">${holderMetrics.pnl_usd >= 0 ? '+' : ''}${holderMetrics.pnl_usd.toFixed(2)}</div>
                                      <div className="text-xs">{holderMetrics.pnl_percent >= 0 ? '+' : ''}{holderMetrics.pnl_percent.toFixed(1)}%</div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Нет позиции</span>
                                  )}
                                </div>
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Время в сделке</div>
                                <div className="font-semibold">
                                  {formatTime(holderMetrics.holding_time_hours)}
                                </div>
                                {holderMetrics.first_buy_time && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    С {new Date(holderMetrics.first_buy_time).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Текущий баланс</div>
                                <div className="font-semibold">
                                  {formatNumber(holderMetrics.current_balance)} {tokenSymbol || 'TOKEN'}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Куплено: {formatNumber(holderMetrics.total_bought)} | Продано: {formatNumber(holderMetrics.total_sold)}
                                </div>
                              </div>
                            </div>

                            {/* Transactions List */}
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold">
                                  Все транзакции ({holderTransactions?.length || 0})
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => loadHolderTransactions(expandedHolder, true)}
                                  disabled={loadingTransactions}
                                  className="text-xs"
                                >
                                  <RefreshCw className={`h-3 w-3 mr-1 ${loadingTransactions ? 'animate-spin' : ''}`} />
                                  Синхронизировать
                                </Button>
                              </div>
                              {holderTransactions && holderTransactions.length > 0 ? (
                                <div className="space-y-1 max-h-96 overflow-y-auto">
                                  {holderTransactions.map((tx, idx) => (
                                    <div 
                                      key={idx}
                                      className={`flex items-center justify-between p-2 rounded text-sm ${
                                        tx.type === 'buy' ? 'bg-green-50' : 'bg-red-50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        {tx.type === 'buy' ? (
                                          <TrendingUp className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <TrendingDown className="h-4 w-4 text-red-600" />
                                        )}
                                        <span className={`font-medium ${tx.type === 'buy' ? 'text-green-700' : 'text-red-700'}`}>
                                          {tx.type.toUpperCase()}
                                        </span>
                                        <span className="text-gray-600 text-xs">
                                          {new Date(tx.timestamp).toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs">
                                        <span>{formatNumber(tx.token_amount || 0)} {tx.token_symbol || tokenSymbol || 'TOKEN'}</span>
                                        <span className="font-mono">{(tx.price_per_token_ton || 0).toFixed(8)} TON</span>
                                        <span className="font-medium">{(tx.ton_amount || 0).toFixed(4)} TON</span>
                                        {tx.tx_hash && (
                                          <a 
                                            href={`https://tonscan.org/tx/${tx.tx_hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                          >
                                            TX
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  {holderMetrics?.history_status === 'transfer_only' ? (
                                    <div className="text-amber-700">
                                      <div className="text-3xl mb-2">📦</div>
                                      <p className="font-semibold mb-2">Получено трансфером</p>
                                      <p className="text-sm text-amber-600">
                                        Токены пришли переводом (airdrop / ручной transfer), без покупки через DEX.
                                        Цена закупки не определяется, что нормально.
                                      </p>
                                    </div>
                                  ) : holderMetrics?.history_status === 'unavailable' ? (
                                    <div className="text-gray-500">
                                      <div className="text-3xl mb-2">📜</div>
                                      <p className="font-semibold mb-2">История транзакций недоступна</p>
                                      <p className="text-sm text-gray-400">
                                        Ни Toncenter, ни TonAPI, ни liteserver не вернули swap'ов.
                                        <br/>
                                        Нажмите "Обновить" для повторной проверки.
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="text-gray-500">
                                      <p>Нет транзакций для этого кошелька</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>Нет данных о транзакциях</p>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TokenTracker;
